/**
 * 인천 시군구 지도 path 데이터.
 *
 * 디자이너가 시군구별로 분리해서 export한 SVG들을 318×281 캔버스 안에 절대 좌표로 배치.
 * 좌표는 Figma 디자인(`16941:187724` 컴포넌트)의 메타데이터에서 inset% → px 변환.
 *
 * 특이사항: 중구(28110)는 path 2개 (영종도 + 본토). 같은 code, 다른 좌표/SVG.
 *   라벨도 영역별로 2개지만 둘 다 같은 시군구로 클릭 동작 (같은 카드 스크롤).
 */

import emptyUrl from '@/assets/maps/인천/empty.svg'
import gyeyangguUrl from '@/assets/maps/인천/계양구.svg'
import namdongguUrl from '@/assets/maps/인천/남동구.svg'
import michuholguUrl from '@/assets/maps/인천/미추홀구.svg'
import bupyeongguUrl from '@/assets/maps/인천/부평구.svg'
import seoguUrl from '@/assets/maps/인천/서구.svg'
import yeonsuguUrl from '@/assets/maps/인천/연수구.svg'
import jungguAUrl from '@/assets/maps/인천/중구-1.svg'
import jungguBUrl from '@/assets/maps/인천/중구-2.svg'

import type { RegionCode } from '../types/region'

export const INCHEON_CANVAS = { width: 318, height: 281 } as const

export interface IncheonPathData {
  /**
   * 표준 행정구역 코드 (5자리). 중구 영종도/본토는 같은 code 공유.
   * 라벨/인터랙션 없는 path(예: empty.svg)는 빈 문자열.
   */
  code: RegionCode
  /** 시군구 표시 이름 (라벨 텍스트) — label 있을 때만 의미 있음 */
  name: string
  /** SVG asset URL */
  svgUrl: string
  /** SVG 자체 viewBox 너비 */
  width: number
  /** SVG 자체 viewBox 높이 */
  height: number
  /** 캔버스 기준 SVG 좌상단 x 좌표 (px) */
  x: number
  /** 캔버스 기준 y 좌표 (px) */
  y: number
  /**
   * 라벨 위치 (캔버스 기준 절대 좌표). undefined면 라벨/클릭 인터랙션 없는 path.
   * 라벨이 있으면 텍스트 중앙이 (x, y)에 위치.
   */
  label?: { x: number; y: number }
}

/** 인천 7개 시군구 — 중구는 path 2개로 분리, 데이터상 같은 code */
export const INCHEON_REGIONS: readonly IncheonPathData[] = [
  // 중구 — 영종도 (좌측 큼)
  { code: '28110', name: '중구', svgUrl: jungguAUrl, x: 3, y: 100, width: 142, height: 121, label: { x: 80.5, y: 155 } },
  // 중구 — 본토 (중간 작)
  { code: '28110', name: '중구', svgUrl: jungguBUrl, x: 149, y: 154, width: 42, height: 53, label: { x: 168.5, y: 182 } },
  // 미추홀구
  {
    code: '28177',
    name: '미추홀구',
    svgUrl: michuholguUrl,
    x: 183,
    y: 151,
    width: 57,
    height: 52,
    label: { x: 210.5, y: 182 },
  },
  // 연수구 (남쪽 큼)
  { code: '28185', name: '연수구', svgUrl: yeonsuguUrl, x: 160, y: 194, width: 86, height: 85, label: { x: 194, y: 236 } },
  // 남동구 (우하단)
  { code: '28200', name: '남동구', svgUrl: namdongguUrl, x: 215, y: 158, width: 91, height: 97, label: { x: 268, y: 203 } },
  // 부평구 (우중간)
  { code: '28237', name: '부평구', svgUrl: bupyeongguUrl, x: 226, y: 104, width: 68, height: 66, label: { x: 257, y: 136 } },
  // 계양구 (우상단)
  { code: '28245', name: '계양구', svgUrl: gyeyangguUrl, x: 223, y: 38, width: 91, height: 74, label: { x: 268, y: 74 } },
  // 서구 (중상단 큼)
  { code: '28260', name: '서구', svgUrl: seoguUrl, x: 144, y: 3, width: 117, height: 157, label: { x: 195.5, y: 73 } },
  // empty — 라벨 없는 path (인천 본토 미추홀구 좌측, 디자이너가 시각적 일관성 위해 둠 / 추정 동구 영역)
  { code: '', name: '', svgUrl: emptyUrl, x: 157, y: 137, width: 55, height: 37 },
] as const

/**
 * 지도 region.code → 백엔드 SiGunGu.name 매핑.
 * 라벨 클릭 시 SiGunGuGroup 카드 스크롤에 사용.
 * 중구는 path가 두 개지만 매핑은 한 개 (28110 → 중구).
 */
export const INCHEON_CODE_TO_SIGUNGU_NAME: Record<string, string> = {
  '28110': '중구',
  '28177': '미추홀구',
  '28185': '연수구',
  '28200': '남동구',
  '28237': '부평구',
  '28245': '계양구',
  '28260': '서구',
}
